import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  DatePicker,
  Button,
  Empty,
  Card,
  Grid,
} from "antd";
import { RedoOutlined, CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useLoading } from "context/loadingContext";
import { ListMyTransactionFeeShares } from "functions/merchant/feeShareRule";
import moneyFormat from "functions/utils/moneyFormat";
import TablePagination from "components/pagination/tablePagination";
import { bg_button, bg_shadow, bg_text, text_color } from "constant/css";

const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const MerchantSplitHistory = () => {
  const { showLoading, hideLoading } = useLoading();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({ byRule: [] });
  const [paginationData, setPaginationData] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);

  useEffect(() => {
    fetchShares();
    // eslint-disable-next-line
  }, [page, pageSize]);

  const fetchShares = async () => {
    showLoading();
    try {
      const parts = [`page=${page}`, `size=${pageSize}`];
      if (dateRange && dateRange[0] && dateRange[1]) {
        const from = dayjs(dateRange[0])
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
        const to = dayjs(dateRange[1])
          .endOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
        parts.push(`createdAt=${encodeURIComponent(`${from}->${to}`)}`);
      }
      const response = await ListMyTransactionFeeShares(parts.join("&"));
      if (response && response.success) {
        setData(response.data || []);
        setPaginationData(response.pagination || {});
        setSummary(response.summary || { byRule: [] });
      } else {
        setData([]);
        setSummary({ byRule: [] });
      }
    } finally {
      hideLoading();
    }
  };

  const refresh = () => {
    setPage(1);
    fetchShares();
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      width: 60,
      render: (v) => <span className="text-xs text-gray-700">{v}</span>,
    },
    {
      title: "Settle",
      dataIndex: "merchantSettleId",
      width: 80,
      render: (v) => (
        <Tag color="default" className="text-xs">
          {v}
        </Tag>
      ),
    },
    {
      title: "Гүйлгээ",
      dataIndex: "transactionId",
      width: 90,
      render: (v) => (
        <Tag color="geekblue" className="text-xs">
          {v}
        </Tag>
      ),
    },
    {
      title: "Хүлээн авагч",
      dataIndex: "recipientName",
      render: (v, r) => (
        <div>
          <div className="text-xs font-semibold text-gray-800">{v || "-"}</div>
          <div className="text-[10px] text-gray-500">
            {r.accountNumber} / {r.bankCode}
          </div>
        </div>
      ),
    },
    {
      title: "Эх fee",
      dataIndex: "merchantFee",
      align: "right",
      width: 110,
      render: (v) => (
        <span className="text-xs text-gray-600">
          {moneyFormat(parseFloat(v || 0))}₮
        </span>
      ),
    },
    {
      title: "%",
      dataIndex: "percent",
      align: "right",
      width: 70,
      render: (v) => (
        <Tag color="cyan" className="text-xs font-semibold">
          {parseFloat(v)}%
        </Tag>
      ),
    },
    {
      title: "Авсан",
      dataIndex: "amount",
      align: "right",
      width: 130,
      render: (v) => (
        <span className={`text-sm font-bold ${text_color}`}>
          {moneyFormat(parseFloat(v || 0))}₮
        </span>
      ),
    },
    {
      title: "Огноо",
      dataIndex: "createdAt",
      width: 140,
      render: (v) => (
        <span className="text-xs text-gray-700">
          {v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-"}
        </span>
      ),
    },
  ];

  const renderSummaryCards = () => {
    if (!summary.byRule || summary.byRule.length === 0) return null;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-4">
        {summary.byRule.map((r) => (
          <Card
            key={r.feeShareRuleId}
            size="small"
            className={`${bg_shadow} rounded-xl border-0`}
            styles={{ body: { padding: 14 } }}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-gray-800 truncate">
                  {r.recipientName || "-"}
                </div>
                <div className="text-[10px] text-gray-500 truncate">
                  {r.accountNumber}
                </div>
              </div>
              <Tag color="cyan" className="text-[10px] m-0">
                {r.count} ширхэг
              </Tag>
            </div>
            <div className={`text-xl font-extrabold ${bg_text}`}>
              {moneyFormat(parseFloat(r.total || 0))}₮
            </div>
            <div className="text-[10px] text-gray-500 mt-1">
              Нийлбэр хуваарилалт
            </div>
          </Card>
        ))}
      </div>
    );
  };

  const renderMobileList = () => {
    if (!data || data.length === 0) {
      return (
        <div className="py-10">
          <Empty description="Хуваарилалтын түүх хоосон байна" />
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-2">
        {data.map((row) => (
          <Card
            key={row.id}
            size="small"
            className={`${bg_shadow} rounded-xl border-0`}
            styles={{ body: { padding: 12 } }}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-gray-800 truncate">
                  {row.recipientName || "-"}
                </div>
                <div className="text-[10px] text-gray-500 truncate">
                  {row.accountNumber} / {row.bankCode}
                </div>
              </div>
              <div className="text-right ml-2">
                <div className={`text-base font-extrabold ${text_color}`}>
                  {moneyFormat(parseFloat(row.amount || 0))}₮
                </div>
                <Tag color="cyan" className="m-0 text-[10px] font-semibold">
                  {parseFloat(row.percent)}%
                </Tag>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-100">
              <Tag color="geekblue" className="text-[10px] m-0">
                Tx #{row.transactionId}
              </Tag>
              <Tag color="default" className="text-[10px] m-0">
                Settle #{row.merchantSettleId}
              </Tag>
              <span className="text-[10px] text-gray-500 ml-auto">
                Эх fee: {moneyFormat(parseFloat(row.merchantFee || 0))}₮
              </span>
            </div>

            <div className="text-[10px] text-gray-500 mt-2 flex items-center gap-1">
              <CalendarOutlined />
              {row.createdAt
                ? dayjs(row.createdAt).format("YYYY-MM-DD HH:mm")
                : "-"}
            </div>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className={`p-3 sm:p-4 bg-white rounded-xl ${bg_shadow}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className={`font-extrabold text-lg sm:text-xl ${bg_text}`}>
            Split түүх
          </h2>
          <span className="text-xs text-gray-500">
            Гүйлгээ тус бүрд яаж хуваарилагдсан snapshot
          </span>
        </div>
        <div className="flex flex-col xs:flex-row gap-2 w-full sm:w-auto">
          <RangePicker
            value={dateRange}
            onChange={(v) => setDateRange(v)}
            size="middle"
            allowClear={false}
            className="w-full sm:w-auto"
          />
          <Button
            icon={<RedoOutlined />}
            onClick={refresh}
            className={`${bg_button} text-white border-0 w-full sm:w-auto`}
          >
            Шинэчлэх
          </Button>
        </div>
      </div>

      {renderSummaryCards()}

      {isMobile ? (
        renderMobileList()
      ) : (
        <Table
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={data}
          pagination={false}
          scroll={{ x: "max-content" }}
          locale={{
            emptyText: <Empty description="Хуваарилалтын түүх хоосон байна" />,
          }}
        />
      )}

      <TablePagination
        page={page}
        paginationData={paginationData}
        changePage={setPage}
        changeSize={(_, size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </div>
  );
};

export default MerchantSplitHistory;
